import React from 'react'
import { Button, Heading, Paragraph, Select, Popup, Textarea, Field, cn } from '@quickmd-llc/design-system'
import { Controller, useForm } from 'react-hook-form'

type FormValues = {
  reason: string
  otherText?: string
}

type Props = {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: FormValues) => void
}

const REASONS = [
  { value: '', label: 'Select a reason...', disabled: true },
  {
    value: 'Technical difficulties (audio and/or video problems)',
    label: 'Technical difficulties (audio and/or video problems)',
  },
  {
    value: 'Patient was a no-show for the appointment - never answered the phone or not seen on video',
    label: 'Patient was a no-show for the appointment - never answered the phone or not seen on video',
  },
  {
    value: 'Patient requesting a medicine not available via QuickMD',
    label: 'Patient requesting a medicine not available via QuickMD',
  },
  {
    value: 'Patient with a medical condition not amenable to treatment via telehealth',
    label: 'Patient with a medical condition not amenable to treatment via telehealth',
  },
  {
    value:
      'Patient mismatch - Person on audio or video is not the patient whose chart is associated with the appointment',
    label:
      'Patient mismatch - Person on audio or video is not the patient whose chart is associated with the appointment',
  },
  { value: 'other', label: 'Other' },
]

export const UnresolvedVisitModal: React.FC<Props> = ({ isOpen, onClose, onSubmit }) => {
  const { control, handleSubmit, watch, formState } = useForm<FormValues>({
    defaultValues: { reason: '', otherText: '' },
    mode: 'onChange',
  })
  const reason = watch('reason')
  const otherText = watch('otherText')
  const { errors } = formState
  const otherTextError = Boolean(errors.otherText)

  return (
    <Popup
      onClose={onClose}
      isOpen={isOpen}
      showCloseIcon
      header={{ content: <Heading level='h3'>Reason for unresolved visit</Heading> }}
      body={
        <form className='flex w-full max-w-md flex-col gap-sp-1' onSubmit={handleSubmit(onSubmit)}>
          <Paragraph className='text-text-medium'>
            Please provide a reason for why this visit was not completed.
          </Paragraph>
          <Field>
            <Controller
              name='reason'
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Select
                  className='w-full'
                  style={{ paddingRight: '2rem' }}
                  value={field.value}
                  onChange={(e) => field.onChange((e as any).target.value)}>
                  {REASONS.map((opt) => (
                    <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              )}
            />
          </Field>
          {reason === 'other' && (
            <Field error={otherTextError}>
              {otherTextError ? <Paragraph className='text-text-error'>Max character length exceed</Paragraph> : null}
              <Controller
                name='otherText'
                control={control}
                rules={{ required: true, maxLength: 140 }}
                render={({ field }) => (
                  <Textarea
                    className='mt-sp-2'
                    placeholder='Type a reason...'
                    value={field.value}
                    onChange={(e) => field.onChange((e as any).target.value)}
                  />
                )}
              />
              <Paragraph
                size='sm'
                className={cn('mt-sp-0.5 flex justify-end text-text-dark', otherTextError && 'text-text-error')}>
                {otherText?.length ?? 0}/140
              </Paragraph>
            </Field>
          )}

          <div className='mt-sp-2 flex justify-end gap-sp-2'>
            <Button priority='primary' type='submit' disabled={!reason || (reason === 'other' && !watch('otherText'))}>
              Submit
            </Button>
          </div>
        </form>
      }
    />
  )
}
